import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../axiosInstance';
import { FaPlus, FaEdit, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { utils, writeFile } from 'xlsx';
import { exportBrandedPdf } from '../utils/brandedPdf';
import '../Pages.css';
import Pagination from '../components/Pagination';

// Small helper component for the per-module "select all" checkbox so that we
// can attach a ref and drive the native `indeterminate` DOM attribute (React
// doesn't expose it as a prop).
function ModuleSelectAllCheckbox({ checked, indeterminate, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

 function ManageRoles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [roleDesc, setRoleDesc] = useState('');
  const [msg, setMsg] = useState('');
  const [showMsg, setShowMsg] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [collapsedModules, setCollapsedModules] = useState({});
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Group permissions by module for the assign-permissions UI.
  // Modules are sorted alphabetically by name; permissions within a module are
  // sorted alphabetically by `action` (falling back to name/description).
  const groupedPermissions = useMemo(() => {
    const groups = new Map();
    permissions.forEach(p => {
      const moduleId = p.module_id ?? 'unassigned';
      const moduleName = p.module_name || 'Other';
      if (!groups.has(moduleId)) {
        groups.set(moduleId, { id: moduleId, name: moduleName, permissions: [] });
      }
      groups.get(moduleId).permissions.push(p);
    });
    const arr = Array.from(groups.values());
    arr.forEach(g => {
      g.permissions.sort((a, b) => {
        const ak = (a.action || a.name || a.description || '').toString().toLowerCase();
        const bk = (b.action || b.name || b.description || '').toString().toLowerCase();
        return ak.localeCompare(bk);
      });
    });
    arr.sort((a, b) => (a.name || '').toString().toLowerCase().localeCompare((b.name || '').toString().toLowerCase()));
    return arr;
  }, [permissions]);

  const toggleModuleCollapsed = (moduleId) => {
    setCollapsedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const toggleSelectAllForModule = (modulePerms, allSelected) => {
    const ids = modulePerms.map(p => p.id);
    if (allSelected) {
      // Uncheck every permission in this module
      setSelectedPermissions(prev => prev.filter(id => !ids.includes(id)));
    } else {
      // Add any missing permission ids for this module
      setSelectedPermissions(prev => {
        const set = new Set(prev);
        ids.forEach(id => set.add(id));
        return Array.from(set);
      });
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const res = await axios.get('/permissions');
      setPermissions(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setPermissions([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get('/roles');
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching roles:', e);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setMsg('');
    setShowMsg(false);

    if (!roleName.trim()) {
      setMsg('❌ Role name required');
      setShowMsg(true);
      return;
    }

    try {
      let roleId = editRole ? editRole.id : null;
      if (!editRole) {
        // Create new role
        const res = await axios.post('/roles', {
          name: roleName,
          description: roleDesc
        });
        // Use backend response to get new role's ID
        let newRoleId = null;
        if (res.data && res.data.role && res.data.role.id) {
          newRoleId = res.data.role.id;
        } else if (res.data && res.data.role && res.data.role.name === roleName) {
          // fallback if only name is returned
          await fetchRoles();
          const newRole = roles.find(r => r.name === roleName);
          newRoleId = newRole ? newRole.id : null;
        }
        roleId = newRoleId;
        await fetchRoles();
      } else {
        // Update role name/desc
        await axios.put(`/roles/${editRole.id}`, {
          name: roleName,
          description: roleDesc
        });
        roleId = editRole.id;
        await fetchRoles();
      }
      // Update permissions for the role (even if none selected, to clear)
      if (roleId) {
        await axios.put(`/roles/${roleId}/permissions`, {
          permissionIds: selectedPermissions
        });
      }
      setMsg(`✅ Role ${editRole ? 'updated' : 'added'}`);
      setShowMsg(true);
      setRoleName('');
      setRoleDesc('');
      setSelectedPermissions([]);
      setShowForm(false);
      setEditRole(null);
      fetchRoles();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.error || e.message));
      setShowMsg(true);
    }
  };

  const toggleStatus = async (role) => {
    try {
      await axios.patch(`/roles/${role.id}`, { is_active: !role.is_active });
      setMsg('✅ Role status updated');
      setShowMsg(true);
      fetchRoles();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.error || e.message));
      setShowMsg(true);
    }
  };

  const handleEdit = async (role) => {
    setRoleName(role.name);
    setRoleDesc(role.description || '');
    setEditRole(role);
    setShowForm(true);
    // Fetch role's permissions
    try {
      const res = await axios.get(`/roles/${role.id}/permissions`);
      setSelectedPermissions(res.data.map(p => p.id));
    } catch {
      setSelectedPermissions([]);
    }
  };

  const filteredList = search
    ? roles.filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
    : roles;

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const paginatedList = filteredList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


  const handleExportCSV = () => {
        const csvRows = [['#', 'Role', 'Description'], ...filteredList.map((r, i) => [i + 1, r.name, r.description || '-'])];
    const csv = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'roles.csv';
    a.click();
  };

  const handleExportExcel = () => {
    const ws = utils.json_to_sheet(filteredList.map((r, i) => ({
      '#': i + 1,
      Role: r.name,
      Description: r.description || '-'
    })));
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Roles');
    writeFile(wb, 'roles.xlsx');
  };

  const handleExportPDF = () => exportBrandedPdf({
    title: 'ROLES — REPORT',
    searchValue: search ? `"${search}"` : 'All roles',
    fileName: 'Roles_Report',
    columns: [
      { header: '#', width: 4, align: 'center' }, { header: 'Role', width: 20, bold: true },
      { header: 'Description', width: 60 }, { header: 'Status', width: 10 }
    ],
    rows: filteredList.map((r, i) => [i + 1, r.name, r.description, r.is_active ? 'Active' : 'Inactive'])
  });

  return (
    <div className="page-container">
      <div className="table-controls">
        <input
          placeholder="Search roles…"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div className="button-group">
          <button className="export-button" onClick={() => { setShowForm(true); setEditRole(null); }}>
            <FaPlus style={{ marginRight: 5 }} /> Add Role
          </button>
          <button className="export-button" onClick={handleExportCSV}>Export CSV</button>
          <button className="export-button" onClick={handleExportExcel}>Export Excel</button>
          <button className="export-button" onClick={handleExportPDF}>Export PDF</button>
        </div>
      </div>

      {showForm && (
        <form className="table-container" onSubmit={handleSubmit}>
          <h3>{editRole ? 'Edit Role' : 'Add New Role'}</h3>
          <div className="form-group">
            <input
              placeholder="Role Name"
              required
              value={roleName}
              onChange={e => setRoleName(e.target.value)}
            />
            <input
              placeholder="Description (optional)"
              value={roleDesc}
              onChange={e => setRoleDesc(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label style={{ fontWeight: 600 }}>Permissions:</label>
            <div className="permission-modules-container">
              {groupedPermissions.map(group => {
                const modulePermIds = group.permissions.map(p => p.id);
                const selectedInModule = modulePermIds.filter(id => selectedPermissions.includes(id));
                const allSelected = modulePermIds.length > 0 && selectedInModule.length === modulePermIds.length;
                const someSelected = selectedInModule.length > 0 && !allSelected;
                const isCollapsed = !!collapsedModules[group.id];
                return (
                  <div key={group.id} className="permission-module-section">
                    <div
                      className="permission-module-header"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: 'var(--bg-secondary, #f3f4f6)',
                        color: 'var(--text-primary, #111)',
                        border: '1px solid var(--border-color, #e3e6ea)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                      onClick={() => toggleModuleCollapsed(group.id)}
                    >
                      <ModuleSelectAllCheckbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={() => toggleSelectAllForModule(group.permissions, allSelected)}
                      />
                      <span style={{ flex: 1, color: 'var(--text-primary, #111)', textTransform: 'capitalize' }}>
                        {group.name}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #555)' }}>
                        {selectedInModule.length}/{modulePermIds.length}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #555)' }}>
                        {isCollapsed ? '▸' : '▾'}
                      </span>
                    </div>
                    {!isCollapsed && (
                      <div className="permission-cards-container" style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
                        {group.permissions.map(p => {
                          const label = p.action || p.description || p.name;
                          const isSelected = selectedPermissions.includes(p.id);
                          return (
                            <div
                              key={p.id}
                              className={`permission-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedPermissions(prev => prev.filter(id => id !== p.id));
                                } else {
                                  setSelectedPermissions(prev => [...prev, p.id]);
                                }
                              }}
                            >
                              <div className="permission-card-header">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPermissions(prev => [...prev, p.id]);
                                    } else {
                                      setSelectedPermissions(prev => prev.filter(id => id !== p.id));
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="permission-name">{label}</span>
                                <span
                                  style={{
                                    marginLeft: '0.5rem',
                                    fontSize: '0.7rem',
                                    padding: '2px 6px',
                                    borderRadius: '10px',
                                    background: '#eef2ff',
                                    color: '#4338ca',
                                    fontWeight: 600
                                  }}
                                  title="Module"
                                >
                                  {group.module_name || 'unassigned'}
                                </span>
                              </div>
                              <code
                                style={{
                                  display: 'inline-block',
                                  marginTop: 4,
                                  fontSize: '0.72rem',
                                  background: '#f4f4f5',
                                  padding: '1px 6px',
                                  borderRadius: 4,
                                  color: '#52525b'
                                }}
                              >
                                {p.name}
                              </code>
                              {p.description && p.description !== label && (
                                <p className="permission-description">{p.description}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <button type="submit" className="export-button">Submit</button>
          <button
            type="button"
            className="btn-cancel"
            onClick={() => { setShowForm(false); setRoleName(''); setRoleDesc(''); setEditRole(null); setSelectedPermissions([]); }}
          >
            Cancel
          </button>
        </form>
      )}

      {showMsg && (
        <div className="message-popup">
          <div className="message-popup-content">
            <p>{msg}</p>
            <button className="close-popup-button" onClick={() => setShowMsg(false)}>Close</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1rem' }}>
        Showing {paginatedList.length} of {filteredList.length} roles {search ? `(filtered from ${roles.length})` : ''}
      </div>

      <div className="table-container">
        <table className="pips-table roles-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Role</th>
              <th>Description</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedList.map((r, i) => (
              <tr key={r.id}>
                <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                <td>{r.name}</td>
                <td>{r.description || '-'}</td>
                <td>{r.is_active ? 'Active' : 'Inactive'}</td>
                <td>
                  <button className="action-button" onClick={() => handleEdit(r)} title="Edit"><FaEdit /></button>
                  <button className="action-button" onClick={() => toggleStatus(r)} title={r.is_active ? 'Deactivate' : 'Activate'}>
                    {r.is_active ? <FaToggleOn className="toggle-icon--on" /> : <FaToggleOff className="toggle-icon--off" />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={filteredList.length}
        itemsPerPage={itemsPerPage}
        itemLabel="role"
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
      />
    </div>
  );
}

export default ManageRoles;
